/* ============================================================
   LitPet Theme — theme.js
   Vanilla JS only. No jQuery, no React, no framer-motion.
   All modules attach at DOMContentLoaded and are idempotent
   (each returns early if its target elements are absent).

   Modules (boot order):
      1. announcementBar       — dismiss + sessionStorage persistence
      2. stickyHeader          — adds .is-scrolled on scroll > 8px
      3. mobileMenu            — hamburger toggle, body lock, Esc close
      4. mobileGroups          — collapsible groups inside the mobile drawer
      5. megamenu              — touch/keyboard open for the desktop megamenu
      6. Cart                  — drawer open/close + AJAX add/change/refresh
      7. accordion             — generic .litpet-accordion__trigger toggle
      8. scrollReveal          — IntersectionObserver fade-up, reduced-motion safe
      9. marqueeFocus          — pause keyboard-focused marquee tracks
     10. carousel              — hero pointer-drag + autoplay carousel
     11. dragScroller          — featured-products / collection drag scroll + arrows
     12. autoScroller          — as-seen-in infinite-loop rAF scroller
     13. pdpGallery            — product thumbnail swap + fullscreen lightbox
     14. pdpDetailsAccordion   — product 5-tab single-open accordion
     15. pdpFaq                — product FAQ multi-open accordion
     16. pdpTestimonialsTabs   — furry testimonials tab switcher
     17. pdpQty                — quantity stepper + live price reactivity
     18. pdpVariantPicker      — variant select → price/availability/URL sync
     19. pdpReviews            — review sort dropdown + show-more
     20. petToggle             — Dog/Cat toggle on collection + health-need pages
     21. cartAddIntercept      — route <form action="/cart/add"> through AJAX

   Public namespace:
     window.LitPet = { cart: { open, close, refresh, add, change } }
============================================================ */

(function () {
  'use strict';

  // -----------------------------------------------------------
  // Shared helpers
  // -----------------------------------------------------------

  const prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function on(el, evt, fn, opts) {
    if (!el) return;
    el.addEventListener(evt, fn, opts || false);
  }

  function trapFocus(container, evt) {
    if (evt.key !== 'Tab') return;
    const focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last  = focusables[focusables.length - 1];
    if (evt.shiftKey && document.activeElement === first) {
      last.focus(); evt.preventDefault();
    } else if (!evt.shiftKey && document.activeElement === last) {
      first.focus(); evt.preventDefault();
    }
  }

  function lockBody() { document.body.style.overflow = 'hidden'; }
  function unlockBody() { document.body.style.overflow = ''; }

  // -----------------------------------------------------------
  // 1. Announcement bar — dismiss
  // -----------------------------------------------------------

  function initAnnouncementBar() {
    const bar = $('[data-litpet-announcement]');
    if (!bar) return;
    if (sessionStorage.getItem('litpet:announcement-closed') === '1') {
      bar.hidden = true;
      return;
    }
    const close = $('[data-litpet-announcement-close]', bar);
    on(close, 'click', function () {
      bar.hidden = true;
      sessionStorage.setItem('litpet:announcement-closed', '1');
    });
  }

  // -----------------------------------------------------------
  // 2. Sticky header
  // -----------------------------------------------------------

  function initStickyHeader() {
    const header = $('[data-litpet-header]');
    if (!header) return;
    if (header.dataset.sticky !== 'true') return;

    let ticking = false;
    function update() {
      if (window.scrollY > 8) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
      ticking = false;
    }
    update();
    on(window, 'scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  // -----------------------------------------------------------
  // 3. Mobile menu (hamburger toggle)
  // -----------------------------------------------------------

  function initMobileMenu() {
    const btn = $('[data-litpet-hamburger]');
    const nav = document.getElementById('LitpetMobileNav');
    if (!btn || !nav) return;

    function set(open) {
      nav.setAttribute('data-open', open ? 'true' : 'false');
      nav.setAttribute('aria-hidden', open ? 'false' : 'true');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      if (open) lockBody(); else unlockBody();
    }
    on(btn, 'click', function () {
      const next = nav.getAttribute('data-open') !== 'true';
      set(next);
    });
    // Close on link click
    $$('a', nav).forEach(function (a) { on(a, 'click', function () { set(false); }); });
    // Close on Escape
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && nav.getAttribute('data-open') === 'true') set(false);
    });
  }

  // -----------------------------------------------------------
  // 4. Mobile menu groups (Pet Health Needs accordion)
  // -----------------------------------------------------------

  function initMobileGroups() {
    $$('[data-litpet-mobile-group]').forEach(function (trigger) {
      const body = trigger.nextElementSibling;
      if (!body) return;
      on(trigger, 'click', function () {
        const open = body.getAttribute('data-open') === 'true';
        body.setAttribute('data-open', open ? 'false' : 'true');
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  // -----------------------------------------------------------
  // 5. Megamenu — desktop click/touch open (hover is CSS-only)
  // -----------------------------------------------------------

  function initMegamenu() {
    $$('[data-litpet-megamenu]').forEach(function (menu) {
      const trigger = $('[data-litpet-megamenu-trigger]', menu);
      if (!trigger) return;
      on(trigger, 'click', function (e) {
        // Only intercept on touch / small screens; desktop relies on :hover
        if (window.matchMedia('(hover: hover)').matches) return;
        e.preventDefault();
        const open = menu.getAttribute('data-open') === 'true';
        menu.setAttribute('data-open', open ? 'false' : 'true');
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
      on(document, 'click', function (e) {
        if (!menu.contains(e.target)) {
          menu.setAttribute('data-open', 'false');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
      on(document, 'keydown', function (e) {
        if (e.key === 'Escape') {
          menu.setAttribute('data-open', 'false');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  // -----------------------------------------------------------
  // 6. Cart drawer
  // -----------------------------------------------------------

  const Cart = (function () {
    const drawer = function () { return document.getElementById('LitpetCartDrawer'); };
    const badge  = function () { return $('[data-litpet-cart-count]'); };
    const countT = function () { return $('[data-litpet-cart-count-text]'); };

    function setOpen(open) {
      const d = drawer();
      if (!d) return;
      d.setAttribute('data-open', open ? 'true' : 'false');
      d.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) {
        lockBody();
        const panel = $('.litpet-cart-drawer__panel', d);
        if (panel) panel.focus();
        on(document, 'keydown', escClose);
      } else {
        unlockBody();
        document.removeEventListener('keydown', escClose);
      }
    }
    function escClose(e) {
      if (e.key === 'Escape') setOpen(false);
    }

    function updateBadge(count) {
      const b = badge();
      if (b) {
        b.textContent = count;
        if (count > 0) b.removeAttribute('hidden'); else b.setAttribute('hidden', '');
      }
      const t = countT();
      if (t) t.textContent = '(' + count + ')';
    }

    function getCart() {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); });
    }

    // Re-fetch the drawer markup via Section Rendering API.
    // We don't have a `cart-drawer` section yet (it's a snippet), so the
    // approach is: get /cart.js JSON, update badge, then re-render the
    // drawer's body+footer by reloading the page's cart-drawer snippet
    // via the cart page section if available, otherwise just update the
    // counts and subtotal in-place.
    function refresh() {
      return getCart().then(function (cart) {
        updateBadge(cart.item_count);
        // In-place subtotal update
        const subtotal = $('[data-litpet-cart-subtotal]');
        if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
        // Update shipping progress text
        updateShippingProgress(cart.total_price);
        // If cart became empty / non-empty, the markup needs the full re-render —
        // which is delivered via the Section Rendering API call below in Phase 4
        // once the cart section is in place. For now, refresh the drawer markup
        // by reloading the snippet via a hidden fetch.
        return refreshDrawerMarkup().then(function () { return cart; });
      });
    }

    function refreshDrawerMarkup() {
      // The cart drawer is a globally-rendered snippet, not a section, so it
      // has no Section Rendering API URL of its own. We re-fetch the current
      // page and swap in the fresh #LitpetCartDrawer markup. Slightly heavier
      // than ?sections=, but reliable. Future optimisation: promote the
      // drawer to a section and target it via the Section Rendering API.
      return fetch(window.location.pathname, { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          const parser = new DOMParser();
          const doc    = parser.parseFromString(html, 'text/html');
          const fresh  = doc.getElementById('LitpetCartDrawer');
          const existing = drawer();
          if (fresh && existing) {
            // Preserve open state
            const wasOpen = existing.getAttribute('data-open') === 'true';
            existing.innerHTML = fresh.innerHTML;
            if (wasOpen) setOpen(true);
            // Re-bind handlers (drawer is fresh markup)
            bindDrawerHandlers();
          }
        })
        .catch(function () { /* swallow */ });
    }

    function updateShippingProgress(totalPriceCents) {
      const wrap = $('[data-litpet-shipping-progress]');
      if (!wrap) return;
      const fill = $('.litpet-cart-drawer__bar-fill', wrap);
      const thresholdMeta = document.documentElement.getAttribute('data-free-shipping-threshold');
      const threshold = thresholdMeta ? parseInt(thresholdMeta, 10) * 100 : 4000;
      if (!fill) return;
      const pct = Math.min(100, (totalPriceCents / threshold) * 100);
      fill.style.width = pct + '%';
    }

    function formatMoney(cents) {
      // Minimal GBP formatter — matches Shopify's default money format for v1.
      return '£' + (cents / 100).toFixed(2);
    }

    function add(variantId, quantity, properties) {
      const body = { items: [{ id: variantId, quantity: quantity || 1 }] };
      if (properties) body.items[0].properties = properties;
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      })
      .then(function (data) {
        return refresh().then(function () { setOpen(true); return data; });
      });
    }

    function change(line, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: line, quantity: quantity })
      })
      .then(function (r) { return r.json(); })
      .then(function () { return refresh(); });
    }

    function bindDrawerHandlers() {
      const d = drawer();
      if (!d) return;
      // Close
      $$('[data-litpet-cart-close]', d).forEach(function (el) {
        on(el, 'click', function (e) { e.preventDefault(); setOpen(false); });
      });
      // Quantity stepper
      $$('[data-litpet-qty]', d).forEach(function (el) {
        on(el, 'click', function (e) {
          const action = el.getAttribute('data-litpet-qty');
          if (action === 'input') return;
          const key = el.getAttribute('data-line-key');
          const input = d.querySelector('[data-litpet-qty="input"][data-line-key="' + key + '"]');
          if (!input) return;
          let q = parseInt(input.value, 10) || 0;
          q = action === 'increment' ? q + 1 : Math.max(0, q - 1);
          input.value = q;
          change(key, q);
        });
      });
      $$('[data-litpet-qty="input"]', d).forEach(function (input) {
        on(input, 'change', function () {
          const key = input.getAttribute('data-line-key');
          const q   = Math.max(0, parseInt(input.value, 10) || 0);
          change(key, q);
        });
      });
      // Remove
      $$('[data-litpet-cart-remove]', d).forEach(function (btn) {
        on(btn, 'click', function () {
          const key = btn.getAttribute('data-line-key');
          change(key, 0);
        });
      });
      // Focus trap
      const panel = $('.litpet-cart-drawer__panel', d);
      if (panel) {
        on(d, 'keydown', function (e) {
          if (d.getAttribute('data-open') !== 'true') return;
          trapFocus(panel, e);
        });
      }
    }

    function init() {
      bindDrawerHandlers();
      // Open triggers can live anywhere on the page
      $$('[data-litpet-cart-open]').forEach(function (btn) {
        on(btn, 'click', function (e) { e.preventDefault(); setOpen(true); });
      });
    }

    return { init: init, open: function () { setOpen(true); }, close: function () { setOpen(false); }, refresh: refresh, add: add, change: change };
  })();

  // -----------------------------------------------------------
  // 7. Generic accordion ( <button aria-expanded> + panel[data-open] )
  // -----------------------------------------------------------

  function initAccordion() {
    $$('.litpet-accordion__trigger').forEach(function (trigger) {
      const panel = trigger.nextElementSibling;
      if (!panel) return;
      on(trigger, 'click', function () {
        const open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.setAttribute('data-open', open ? 'false' : 'true');
      });
    });
  }

  // -----------------------------------------------------------
  // 8. Scroll reveal (replaces React's <ScrollReveal>)
  // -----------------------------------------------------------

  function initScrollReveal() {
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      $$('[data-reveal]').forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
    $$('[data-reveal]').forEach(function (el) { io.observe(el); });
  }

  // -----------------------------------------------------------
  // 9. Marquee pause on focus-within (CSS handles pause-on-hover)
  // -----------------------------------------------------------

  function initMarqueeFocus() {
    $$('.litpet-marquee').forEach(function (m) {
      const track = $('.litpet-marquee__track', m);
      if (!track) return;
      on(m, 'focusin', function () { track.style.animationPlayState = 'paused'; });
      on(m, 'focusout', function () { track.style.animationPlayState = ''; });
    });
  }

  // -----------------------------------------------------------
  // 10a. Hero carousel — vanilla pointer-drag + autoplay
  //      Replaces Embla. Targets [data-litpet-carousel].
  // -----------------------------------------------------------

  function initCarousel() {
    $$('[data-litpet-carousel]').forEach(function (root) {
      const track    = $('[data-litpet-carousel-track]', root);
      const viewport = $('[data-litpet-carousel-viewport]', root);
      const slides   = $$('[data-litpet-carousel-slide]', root);
      const dots     = $$('[data-litpet-carousel-dot]', root);
      if (!track || slides.length < 2) return;

      // Mobile only: size the viewport to the active slide so a shorter
      // slide (e.g. flat banner) doesn't leave an empty gap below it.
      // Desktop is left untouched (slides keep their stretched height).
      const mobileMQ = window.matchMedia('(max-width: 639px)');
      function syncHeight() {
        if (!viewport) return;
        if (mobileMQ.matches) {
          viewport.style.height = slides[index].offsetHeight + 'px';
        } else {
          viewport.style.height = '';
        }
      }

      let index = 0;
      const total = slides.length;
      const intervalMs = parseInt(root.dataset.autoplayMs || '5500', 10);
      let timer = null;
      let isHovering = false;
      let isPointerDown = false;

      function setIndex(i, immediate) {
        index = (i + total) % total;
        const tx = -index * 100;
        track.style.transition = immediate ? 'none' : '';
        track.style.transform  = 'translateX(' + tx + '%)';
        if (immediate) {
          // Force reflow then re-enable transitions
          /* eslint-disable-next-line no-unused-expressions */
          track.offsetHeight;
          track.style.transition = '';
        }
        dots.forEach(function (d, di) {
          if (di === index) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
        syncHeight();
      }
      function next() { setIndex(index + 1); }
      function prev() { setIndex(index - 1); }

      // Re-measure when the viewport changes or lazy images finish loading.
      on(window, 'resize', syncHeight);
      slides.forEach(function (s) {
        const im = s.querySelector('img');
        if (im && !im.complete) on(im, 'load', syncHeight);
      });

      function start() {
        if (prefersReducedMotion || intervalMs <= 0) return;
        stop();
        timer = window.setInterval(function () {
          if (!isHovering && !isPointerDown && !document.hidden) next();
        }, intervalMs);
      }
      function stop() {
        if (timer) { window.clearInterval(timer); timer = null; }
      }

      // Dot clicks
      dots.forEach(function (d) {
        on(d, 'click', function () {
          const target = parseInt(d.dataset.index, 10);
          if (!isNaN(target)) setIndex(target);
        });
      });

      // Pointer drag
      let startX = 0, lastX = 0, dragging = false;
      on(track, 'pointerdown', function (e) {
        if (e.button !== undefined && e.button !== 0) return;
        if (e.target.closest('a, button')) return;
        isPointerDown = true;
        dragging = true;
        startX = lastX = e.clientX;
        track.style.transition = 'none';
        try { track.setPointerCapture(e.pointerId); } catch (_) {}
      });
      on(track, 'pointermove', function (e) {
        if (!dragging) return;
        lastX = e.clientX;
        const dx = lastX - startX;
        const pct = (dx / track.clientWidth) * 100;
        track.style.transform = 'translateX(' + (-index * 100 + pct) + '%)';
      });
      function endDrag(e) {
        if (!dragging) return;
        const dx = lastX - startX;
        track.style.transition = '';
        if (Math.abs(dx) > track.clientWidth * 0.15) {
          if (dx < 0) next(); else prev();
        } else {
          setIndex(index);
        }
        dragging = false;
        isPointerDown = false;
        try { track.releasePointerCapture(e.pointerId); } catch (_) {}
      }
      on(track, 'pointerup', endDrag);
      on(track, 'pointercancel', endDrag);
      on(track, 'pointerleave', function (e) { if (dragging) endDrag(e); });

      // Pause on hover
      on(root, 'mouseenter', function () { isHovering = true; });
      on(root, 'mouseleave', function () { isHovering = false; });

      // Visibility API — pause when tab hidden
      on(document, 'visibilitychange', function () {
        if (document.hidden) stop(); else start();
      });

      // Init
      setIndex(0, true);
      start();
    });
  }

  // -----------------------------------------------------------
  // 10b. Drag scroller — featured-products / collection grids
  //      Pointer-drag horizontal scroll + arrow buttons.
  // -----------------------------------------------------------

  function initDragScroller() {
    $$('[data-litpet-drag-scroller]').forEach(function (root) {
      const track = $('[data-litpet-drag-scroller-track]', root);
      if (!track) return;
      const prevBtn = $('[data-litpet-scroll-prev]', root);
      const nextBtn = $('[data-litpet-scroll-next]', root);

      let isDown = false, startX = 0, startScroll = 0, moved = false;
      on(track, 'pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        isDown = true;
        moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
      });
      on(track, 'pointermove', function (e) {
        if (!isDown) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) < 8 && !moved) return;
        moved = true;
        try { track.setPointerCapture(e.pointerId); } catch (_) {}
        track.scrollLeft = startScroll - dx;
      });
      function endDrag(e) {
        isDown = false;
        try { track.releasePointerCapture(e.pointerId); } catch (_) {}
        // Suppress accidental click after drag
        if (moved) {
          const suppress = function (ev) { ev.preventDefault(); ev.stopPropagation(); track.removeEventListener('click', suppress, true); };
          track.addEventListener('click', suppress, true);
        }
      }
      on(track, 'pointerup', endDrag);
      on(track, 'pointercancel', endDrag);
      on(track, 'pointerleave', endDrag);

      function scrollBy(dir) {
        const amount = Math.round(track.clientWidth * 0.8) * dir;
        track.scrollBy({ left: amount, behavior: 'smooth' });
      }
      on(prevBtn, 'click', function () { scrollBy(-1); });
      on(nextBtn, 'click', function () { scrollBy(1); });
    });
  }

  // -----------------------------------------------------------
  // 10c. Auto-scroller — as-seen-in style infinite-loop scroller
  //      Targets [data-litpet-auto-scroller]; track = [data-litpet-auto-scroller-track]
  // -----------------------------------------------------------

  function initAutoScroller() {
    if (prefersReducedMotion) return;
    $$('[data-litpet-auto-scroller]').forEach(function (root) {
      const track = $('[data-litpet-auto-scroller-track]', root);
      if (!track) return;

      const speedPxPerSec = parseFloat(root.dataset.speed || '40');
      let raf = null, last = null;
      let paused = false, isPointerDown = false;
      let startX = 0, startScroll = 0, moved = false;

      function initScroll() {
        // Track is rendered ×3 — start at the middle third so user can drag both ways.
        track.parentElement.scrollLeft = track.scrollWidth / 3;
      }
      function loop(t) {
        if (last === null) last = t;
        const dt = (t - last) / 1000;
        last = t;
        if (!paused && !isPointerDown) {
          const wrap = track.parentElement;
          wrap.scrollLeft += speedPxPerSec * dt;
          const third = track.scrollWidth / 3;
          if (wrap.scrollLeft >= third * 2) wrap.scrollLeft -= third;
          else if (wrap.scrollLeft <= third * 0.5) wrap.scrollLeft += third;
        }
        raf = window.requestAnimationFrame(loop);
      }

      on(root, 'mouseenter', function () { paused = true; });
      on(root, 'mouseleave', function () { paused = false; });

      const wrap = track.parentElement;
      on(wrap, 'pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        isPointerDown = true;
        moved = false;
        startX = e.clientX;
        startScroll = wrap.scrollLeft;
      });
      on(wrap, 'pointermove', function (e) {
        if (!isPointerDown) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) < 4 && !moved) return;
        moved = true;
        try { wrap.setPointerCapture(e.pointerId); } catch (_) {}
        wrap.scrollLeft = startScroll - dx;
      });
      function endPointer(e) {
        isPointerDown = false;
        try { wrap.releasePointerCapture(e.pointerId); } catch (_) {}
        if (moved) {
          const suppress = function (ev) { ev.preventDefault(); ev.stopPropagation(); wrap.removeEventListener('click', suppress, true); };
          wrap.addEventListener('click', suppress, true);
        }
      }
      on(wrap, 'pointerup', endPointer);
      on(wrap, 'pointercancel', endPointer);
      on(wrap, 'pointerleave', function (e) { if (isPointerDown) endPointer(e); });

      // Defer init by one frame to ensure layout settled
      window.requestAnimationFrame(function () { initScroll(); raf = window.requestAnimationFrame(loop); });
    });
  }

  // -----------------------------------------------------------
  // 10d. Product page gallery — thumbnail swap + lightbox
  // -----------------------------------------------------------

  function initPdpGallery() {
    const gallery = $('[data-litpet-gallery]');
    if (!gallery) return;
    const mainImg = $('#LitpetPdpMainImg', gallery);
    const thumbs  = $$('[data-litpet-gallery-thumb]', gallery);
    if (!mainImg) return;

    let currentIndex = 0;
    function setActive(index) {
      currentIndex = index;
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      const target = thumbs[index];
      if (!target) return;
      const fullSrc = target.dataset.full;
      if (fullSrc) {
        // The main <img> is rendered with a `srcset` (Shopify's image_tag), and
        // the browser chooses what to display from `srcset`, treating `src` as a
        // fallback. Setting `src` alone leaves the visible image unchanged, so
        // drop `srcset` to make the browser honour the photo we just selected.
        mainImg.removeAttribute('srcset');
        mainImg.src = fullSrc;
      }
    }
    thumbs.forEach(function (t, i) {
      on(t, 'click', function () { setActive(i); });
    });

    // Lightbox
    const openers = $$('[data-litpet-gallery-open]', gallery);
    function openLightbox() {
      let lb = document.getElementById('LitpetLightbox');
      const firstBuild = !lb;
      if (!lb) {
        lb = document.createElement('div');
        lb.id = 'LitpetLightbox';
        lb.className = 'litpet-lightbox';
        lb.setAttribute('role', 'dialog');
        lb.setAttribute('aria-modal', 'true');

        var thumbsHtml = '';
        if (thumbs.length > 1) {
          lb.classList.add('has-thumbs');
          thumbsHtml = '<div class="litpet-lightbox__thumbs" role="tablist" aria-label="Product images">';
          thumbs.forEach(function (t, i) {
            var tImg = t.querySelector('img');
            var tSrc = tImg ? tImg.getAttribute('src') : (t.dataset.full || '');
            thumbsHtml +=
              '<button type="button" class="litpet-lightbox__thumb" data-litpet-lightbox-thumb data-index="' + i + '" role="tab" aria-label="View image ' + (i + 1) + '">' +
                '<img src="' + tSrc + '" alt="" loading="lazy">' +
              '</button>';
          });
          thumbsHtml += '</div>';
        }

        lb.innerHTML =
          '<button type="button" class="litpet-lightbox__close" aria-label="Close">×</button>' +
          '<button type="button" class="litpet-lightbox__prev" aria-label="Previous image">‹</button>' +
          '<img class="litpet-lightbox__img" alt="">' +
          '<button type="button" class="litpet-lightbox__next" aria-label="Next image">›</button>' +
          '<span class="litpet-lightbox__counter"></span>' +
          thumbsHtml;
        document.body.appendChild(lb);
      }
      const img = $('.litpet-lightbox__img', lb);
      const counter = $('.litpet-lightbox__counter', lb);
      const lbThumbs = $$('[data-litpet-lightbox-thumb]', lb);
      function paint() {
        const t = thumbs[currentIndex] || thumbs[0];
        if (!t || !img) return;
        img.src = t.dataset.full || (mainImg ? mainImg.src : '');
        img.alt = mainImg ? mainImg.alt : '';
        if (counter) counter.textContent = (currentIndex + 1) + ' / ' + Math.max(thumbs.length, 1);
        lbThumbs.forEach(function (lt, i) {
          var active = i === currentIndex;
          lt.classList.toggle('is-active', active);
          lt.setAttribute('aria-selected', active ? 'true' : 'false');
          if (active && lt.scrollIntoView) lt.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      }
      function go(delta) {
        if (!thumbs.length) return;
        currentIndex = (currentIndex + delta + thumbs.length) % thumbs.length;
        setActive(currentIndex);
        paint();
      }
      function close() {
        lb.classList.remove('is-open');
        unlockBody();
      }
      function onKey(e) {
        if (!lb.classList.contains('is-open')) return;
        if (e.key === 'Escape') close();
        if (e.key === 'ArrowLeft')  go(-1);
        if (e.key === 'ArrowRight') go(1);
      }

      // Bind interaction handlers once. The lightbox element persists between
      // opens (cached via getElementById), so re-binding on every open would
      // stack duplicate listeners and make each tap/swipe jump several images.
      if (firstBuild) {
        on($('.litpet-lightbox__close', lb), 'click', close);
        on($('.litpet-lightbox__prev',  lb), 'click', function () { go(-1); });
        on($('.litpet-lightbox__next',  lb), 'click', function () { go(1); });
        lbThumbs.forEach(function (lt) {
          on(lt, 'click', function () { currentIndex = parseInt(lt.dataset.index, 10) || 0; setActive(currentIndex); paint(); });
        });

        // Touch swipe (mobile): a horizontal drag steps between images.
        // swipe left → next, swipe right → previous.
        var swipeX = 0, swipeY = 0, didSwipe = false;
        on(lb, 'touchstart', function (e) {
          var p = e.changedTouches[0];
          swipeX = p.clientX; swipeY = p.clientY; didSwipe = false;
        }, { passive: true });
        on(lb, 'touchend', function (e) {
          var p = e.changedTouches[0];
          var dx = p.clientX - swipeX;
          var dy = p.clientY - swipeY;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            didSwipe = true;
            go(dx < 0 ? 1 : -1);
          }
        }, { passive: true });

        // Backdrop tap closes — but ignore the click a swipe leaves behind.
        on(lb, 'click', function (e) {
          if (didSwipe) { didSwipe = false; return; }
          if (e.target === lb) close();
        });

        on(document, 'keydown', onKey);
      }

      paint();
      lb.classList.add('is-open');
      lockBody();
    }
    openers.forEach(function (btn) { on(btn, 'click', openLightbox); });
  }

  // -----------------------------------------------------------
  // 10e. Product Details accordion — single-open (one tab at a time)
  // -----------------------------------------------------------

  function initPdpDetailsAccordion() {
    const root = $('[data-litpet-pdp-accordion]');
    if (!root) return;
    const triggers = $$('[data-litpet-pdp-tab]', root);
    triggers.forEach(function (trigger) {
      const panel = trigger.nextElementSibling;
      if (!panel) return;
      on(trigger, 'click', function () {
        const open = trigger.getAttribute('aria-expanded') === 'true';
        // Close everything else
        triggers.forEach(function (t) {
          t.setAttribute('aria-expanded', 'false');
          const p = t.nextElementSibling;
          if (p) p.setAttribute('data-open', 'false');
        });
        if (!open) {
          trigger.setAttribute('aria-expanded', 'true');
          panel.setAttribute('data-open', 'true');
        }
      });
    });
  }

  // -----------------------------------------------------------
  // 10f. Product FAQ — multi-open
  // -----------------------------------------------------------

  function initPdpFaq() {
    $$('[data-litpet-pdp-faq-trigger]').forEach(function (trigger) {
      const item = trigger.closest('.litpet-pdp-faq__item');
      if (!item) return;
      on(trigger, 'click', function () {
        const open = item.getAttribute('data-open') === 'true';
        item.setAttribute('data-open', open ? 'false' : 'true');
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  // -----------------------------------------------------------
  // 10f-2. Product recommendations — fetch via Shopify Recommendations API
  // -----------------------------------------------------------

  function initProductRecommendations() {
    $$('[data-litpet-product-recommendations]').forEach(function (shell) {
      if (shell.querySelector('.litpet-pdp-recommendations__grid')) return; // already rendered server-side
      const url = shell.dataset.url;
      if (!url) return;
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('[data-litpet-product-recommendations]');
          if (fresh) shell.innerHTML = fresh.innerHTML;
        })
        .catch(function () { /* fail silently */ });
    });
  }

  // -----------------------------------------------------------
  // 10g. Testimonials tabs (FurryTestimonials)
  // -----------------------------------------------------------

  function initPdpTestimonialsTabs() {
    const tabsRoot = $('[data-litpet-testimonials-tabs]');
    const stageRoot = $('[data-litpet-testimonials-stage]');
    if (!tabsRoot || !stageRoot) return;
    const tabs = $$('[data-index]', tabsRoot);
    const quotes = $$('[data-index]', stageRoot);
    tabs.forEach(function (tab, i) {
      on(tab, 'click', function () {
        tabs.forEach(function (t, ti) {
          t.classList.toggle('is-active', ti === i);
          t.setAttribute('aria-selected', ti === i ? 'true' : 'false');
        });
        quotes.forEach(function (q, qi) {
          q.classList.toggle('is-active', qi === i);
          q.setAttribute('aria-hidden', qi === i ? 'false' : 'true');
        });
      });
    });
  }

  // -----------------------------------------------------------
  // 10h. PDP quantity stepper + ATC price reactivity
  // -----------------------------------------------------------

  function initPdpQty() {
    const input = $('[data-litpet-pdp-qty-input]');
    const atc   = $('[data-litpet-pdp-atc]');
    const buyNowPrice = $('[data-litpet-pdp-price-display]');
    if (!input) return;

    function formatMoney(cents) { return '£' + (cents / 100).toFixed(2); }
    function refresh() {
      const q = Math.max(1, parseInt(input.value, 10) || 1);
      input.value = q;
      if (atc) {
        const cents = parseInt(atc.dataset.unitPriceCents, 10) || 0;
        const priceEl = $('[data-litpet-pdp-atc-price]', atc);
        if (priceEl) priceEl.textContent = formatMoney(cents * q);
      }
      if (buyNowPrice) {
        const cents = atc ? (parseInt(atc.dataset.unitPriceCents, 10) || 0) : 0;
        buyNowPrice.textContent = formatMoney(cents * q);
      }
    }

    $$('[data-litpet-pdp-qty]').forEach(function (btn) {
      on(btn, 'click', function () {
        const action = btn.getAttribute('data-litpet-pdp-qty');
        const cur = parseInt(input.value, 10) || 1;
        input.value = action === 'increment' ? cur + 1 : Math.max(1, cur - 1);
        refresh();
      });
    });
    on(input, 'change', refresh);
    on(input, 'input',  refresh);
  }

  // -----------------------------------------------------------
  // 10i. PDP variant picker — change variant → update price, image, ATC state
  // -----------------------------------------------------------

  function initPdpVariantPicker() {
    const picker = $('[data-litpet-variant-picker]');
    if (!picker) return;
    const variantIdInput = $('[data-litpet-variant-id]', picker);
    const selects = $$('[data-litpet-variant-option]', picker);
    const atc = $('[data-litpet-pdp-atc]');
    const buyNow = $('[data-litpet-pdp-buy-now]');
    const priceDisplay = $('[data-litpet-pdp-price-display]');
    const atcPrice = $('[data-litpet-pdp-atc-price]');
    const productJsonScript = $('[data-litpet-product-json]');
    if (!productJsonScript) return;
    let productData;
    try { productData = JSON.parse(productJsonScript.textContent); } catch (e) { return; }
    if (!productData || !productData.variants) return;

    function matchVariant() {
      const wanted = selects.map(function (s) { return s.value; });
      return productData.variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === wanted[i]; });
      });
    }
    function update() {
      const v = matchVariant();
      if (!v) return;
      if (variantIdInput) variantIdInput.value = v.id;
      if (atc) {
        atc.dataset.unitPriceCents = v.price;
        atc.disabled = !v.available;
      }
      if (buyNow) buyNow.disabled = !v.available;
      const qInput = $('[data-litpet-pdp-qty-input]');
      const q = Math.max(1, parseInt(qInput && qInput.value, 10) || 1);
      const formatted = '£' + (v.price * q / 100).toFixed(2);
      if (priceDisplay) priceDisplay.textContent = formatted;
      if (atcPrice)     atcPrice.textContent     = formatted;
      // Update URL
      const params = new URLSearchParams(window.location.search);
      params.set('variant', v.id);
      window.history.replaceState({}, '', window.location.pathname + '?' + params.toString());
    }
    selects.forEach(function (s) { on(s, 'change', update); });
  }

  // -----------------------------------------------------------
  // 10j. PDP reviews — sort + show more
  // -----------------------------------------------------------

  function initPdpReviews() {
    const list   = $('[data-litpet-reviews-list]');
    const sort   = $('[data-litpet-reviews-sort]');
    const more   = $('[data-litpet-reviews-more]');
    if (!list) return;

    function cards() { return $$('.litpet-pdp-reviews__card', list); }

    if (sort) {
      on(sort, 'change', function () {
        const mode = sort.value;
        const arr = cards().slice().sort(function (a, b) {
          if (mode === 'highest') return parseInt(b.dataset.stars) - parseInt(a.dataset.stars);
          if (mode === 'lowest')  return parseInt(a.dataset.stars) - parseInt(b.dataset.stars);
          if (mode === 'helpful') return parseInt(b.dataset.helpful) - parseInt(a.dataset.helpful);
          if (mode === 'oldest')  return parseInt(b.dataset.timeRank) - parseInt(a.dataset.timeRank);
          return parseInt(a.dataset.timeRank) - parseInt(b.dataset.timeRank); // 'recent' = original order
        });
        arr.forEach(function (c) { list.appendChild(c); });
      });
    }

    if (more) {
      on(more, 'click', function () {
        cards().forEach(function (c) { c.removeAttribute('hidden'); });
        more.parentElement.style.display = 'none';
      });
    }
  }

  // -----------------------------------------------------------
  // 10k. Pet toggle — collection grid + health-need hero
  //      Reads ?pet=cat|dog from URL on load. Toggles sync URL + filter grid items.
  // -----------------------------------------------------------

  function initPetToggle() {
    function readPet() {
      const params = new URLSearchParams(window.location.search);
      const v = (params.get('pet') || '').toLowerCase();
      return v === 'cat' ? 'cat' : 'dog';
    }

    function applyPet(pet) {
      // Generic grid pet toggle
      const grids = $$('[data-litpet-collection-grid]');
      grids.forEach(function (g) {
        g.parentElement.setAttribute('data-pet-active', pet);
      });
      $$('[data-litpet-pet-toggle] [data-pet]').forEach(function (btn) {
        const isActive = btn.getAttribute('data-pet') === pet;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      // Health-need hero
      const heroes = $$('[data-litpet-health-hero]');
      heroes.forEach(function (h) {
        h.setAttribute('data-pet', pet);
        $$('[data-litpet-health-pet]', h).forEach(function (btn) {
          const isActive = btn.getAttribute('data-litpet-health-pet') === pet;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        // Swap pet-aware content
        $$('[data-litpet-pet-label-plural]', h).forEach(function (el) {
          el.textContent = pet === 'cat' ? 'cats' : 'dogs';
        });
        const sub = $('[data-litpet-pet-sub]', h);
        if (sub) sub.textContent = sub.getAttribute('data-' + pet) || sub.getAttribute('data-dog');
        const desc = $('[data-litpet-pet-desc]', h);
        if (desc) desc.textContent = desc.getAttribute('data-' + pet) || desc.getAttribute('data-dog');
        const signs = $('[data-litpet-pet-signs]', h);
        if (signs) {
          const raw = signs.getAttribute('data-' + pet) || signs.getAttribute('data-dog') || '';
          signs.innerHTML = raw.split('|').map(function (s) {
            return s.trim() ? '<li class="litpet-health-hero__sign"><span class="litpet-health-hero__sign-icon" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 13 4 4L19 7"/></svg></span><span>' + s.trim() + '</span></li>' : '';
          }).join('');
        }
      });
    }

    function setPet(pet, pushUrl) {
      applyPet(pet);
      if (pushUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('pet', pet);
        window.history.replaceState({}, '', url.toString());
      }
    }

    const initial = readPet();
    applyPet(initial);

    $$('[data-litpet-pet-toggle] [data-pet]').forEach(function (btn) {
      on(btn, 'click', function () { setPet(btn.getAttribute('data-pet'), true); });
    });
    $$('[data-litpet-health-pet]').forEach(function (btn) {
      on(btn, 'click', function () { setPet(btn.getAttribute('data-litpet-health-pet'), true); });
    });
  }

  // -----------------------------------------------------------
  // 11. Cart-add form interception — intercept the default Shopify <form
  //     action="/cart/add"> behaviour and route through AJAX so the drawer
  //     opens instead of navigating to /cart.
  // -----------------------------------------------------------

  function initCartAddIntercept() {
    on(document, 'submit', function (e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action || form.action.indexOf('/cart/add') === -1) return;
      e.preventDefault();
      const fd = new FormData(form);
      const variantId = fd.get('id');
      const quantity  = fd.get('quantity') || 1;
      if (!variantId) return;
      const properties = {};
      fd.forEach(function (value, key) {
        if (key.indexOf('properties[') === 0) {
          properties[key.slice(11, -1)] = value;
        }
      });
      const hasProps = Object.keys(properties).length > 0;
      Cart.add(variantId, parseInt(quantity, 10), hasProps ? properties : null)
        .catch(function (err) {
          console.warn('[LitPet] Cart add failed:', err);
          // Fallback: full submit
          form.submit();
        });
    });
  }

  // -----------------------------------------------------------
  // Boot
  // -----------------------------------------------------------

  // -----------------------------------------------------------
  // 14. Stockists — region filter
  // -----------------------------------------------------------

  function initStockists() {
    $$('[data-litpet-stockists]').forEach(function (root) {
      const bar = $('[data-litpet-stockists-filter]', root);
      if (!bar) return;
      const groups = $$('.litpet-stockists__group', root);
      const buttons = $$('.litpet-stockists__filter-btn', bar);
      on(bar, 'click', function (e) {
        const btn = e.target.closest('.litpet-stockists__filter-btn');
        if (!btn) return;
        const region = btn.getAttribute('data-region');
        buttons.forEach(function (b) {
          const active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        groups.forEach(function (g) {
          g.hidden = !(region === 'all' || g.getAttribute('data-region') === region);
        });
      });
    });
  }

  function boot() {
    initAnnouncementBar();
    initStickyHeader();
    initMobileMenu();
    initMobileGroups();
    initMegamenu();
    Cart.init();
    initAccordion();
    initScrollReveal();
    initMarqueeFocus();
    initCarousel();
    initDragScroller();
    initAutoScroller();
    initPdpGallery();
    initPdpDetailsAccordion();
    initPdpFaq();
    initProductRecommendations();
    initPdpTestimonialsTabs();
    initPdpQty();
    initPdpVariantPicker();
    initPdpReviews();
    initPetToggle();
    initCartAddIntercept();
    initStockists();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose for inline usage in product templates
  window.LitPet = window.LitPet || {};
  window.LitPet.cart = {
    open: Cart.open,
    close: Cart.close,
    refresh: Cart.refresh,
    add: Cart.add,
    change: Cart.change
  };
})();
